import { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { PageNavbar } from '@/pages/account';
import { useLocation } from 'react-router-dom';
import { useLayout } from '@/providers';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import { KeenIcon, DataGrid } from '@/components';
import { ConfirmationDialog } from '@/components/dialogBox';
import { berthCategoryValidationSchema } from '@/utils/validations';
import {
  useGetCategoriesQuery,
  useDeleteCategoryMutation,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
} from '@/services/BerthCategoryService';
import { ErrorApiResponse } from '@/types/api.types';
import { IBerthCategoryData } from '@/types/berth-category.types';
import { BerthCategoryModal } from '../berth-management/BerthCategoryModal';
import usePagination from '@/hooks/usePagination';
import { usePermissions } from '@/hooks/usePermissions';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { formatFinnishNumber } from '@/utils';

const formatMeasurementRange = (
  minValue?: number | null,
  maxValue?: number | null
) => {
  const formatValue = (value: number) =>
    formatFinnishNumber(value, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

  if (minValue !== null && minValue !== undefined) {
    if (maxValue !== null && maxValue !== undefined) {
      return `${formatValue(minValue)} - ${formatValue(maxValue)}`;
    }

    return `>${formatValue(minValue)}`;
  }

  return '-';
};

const BerthCategoryManagement = () => {
  const { currentLayout } = useLayout();
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const isHarborDetailsRoute = location.pathname === '/harbors/details';
  const selectedHarborId = useSelector(
    (state: RootState) => state.auth.selectedHarborId
  );
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    category: IBerthCategoryData | null;
    mode: 'view' | 'edit' | 'create';
  }>({ isOpen: false, category: null, mode: 'view' });
  const [confirmDeleteState, setConfirmDeleteState] = useState<{
    isOpen: boolean;
    categoryId: string | null | undefined;
  }>({ isOpen: false, categoryId: null });
  const [formData, setFormData] = useState<Partial<IBerthCategoryData>>({
    name: '',
    description: '',
    min_length: undefined,
    max_length: undefined,
    min_width: undefined,
    max_width: undefined,
    min_depth: undefined,
    max_depth: undefined,
    status: 1,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const { hasPermission } = usePermissions();

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const {
    data: categoriesResponse,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetCategoriesQuery({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedSearch || undefined,
    harbor_id: isHarborDetailsRoute ? selectedHarborId || undefined : undefined,
  });

  useEffect(() => {
    if (debouncedSearch === '') {
      refetch();
    }
  }, [debouncedSearch, refetch]);

  const [createCategory, { isLoading: isCreatingCategory }] =
    useCreateCategoryMutation();
  const [updateCategory] = useUpdateCategoryMutation();
  const [deleteCategory] = useDeleteCategoryMutation();

  useEffect(() => {
    if (categoriesResponse) {
      handleTotalPages(categoriesResponse.totalPages || 1);
      handleTotalItems(
        (categoriesResponse.total ?? categoriesResponse.totalItems) || 0
      );
    }
  }, [categoriesResponse, handleTotalPages, handleTotalItems]);

  const handleOpenModal = (
    category: IBerthCategoryData | null,
    mode: 'view' | 'edit' | 'create'
  ) => {
    if (mode === 'create') {
      setModalState({ isOpen: true, category: null, mode: 'create' });
      setFormData({
        name: '',
        description: '',
        min_length: undefined,
        max_length: undefined,
        min_width: undefined,
        max_width: undefined,
        min_depth: undefined,
        max_depth: undefined,
        status: 1,
        harbor_id: isHarborDetailsRoute ? selectedHarborId || '' : undefined,
      });
      setErrors({});
    } else if (category?.id && (mode === 'view' || mode === 'edit')) {
      setModalState({ isOpen: true, category, mode });
      // No need to set formData here as it will be handled by useGetCategoryByIdQuery in BerthCategoryModal
      setErrors({});
    }
  };

  const handleCloseModal = () => {
    setModalState({ isOpen: false, category: null, mode: 'view' });
    setFormData({
      name: '',
      description: '',
      min_length: undefined,
      max_length: undefined,
      min_width: undefined,
      max_width: undefined,
      min_depth: undefined,
      max_depth: undefined,
      status: 1,
    });
    setErrors({});
  };

  const validateField = useCallback(
    async (name: string, value: unknown) => {
      try {
        await berthCategoryValidationSchema.validateAt(
          name,
          { ...formData, [name]: value },
          { abortEarly: true }
        );
        setErrors((prev) => ({
          ...prev,
          [name]: '',
        }));
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          setErrors((prev) => ({
            ...prev,
            [name]: err.errors[0] || 'Invalid input',
          }));
        }
      }
    },
    [formData]
  );

  const handleSubmit = async (formData: Partial<IBerthCategoryData>) => {
    try {
      await berthCategoryValidationSchema.validate(formData, {
        abortEarly: false,
      });

      const payload = {
        id: formData.id || '',
        name: formData.name || '',
        description: formData.description || '',
        min_length: formData.min_length,
        max_length: formData.max_length,
        min_width: formData.min_width,
        max_width: formData.max_width,
        min_depth: formData.min_depth,
        max_depth: formData.max_depth,
        status: formData.status || 1,
        harbor_id:
          (isHarborDetailsRoute ? selectedHarborId : undefined) ||
          formData.harbor_id ||
          '',
      };

      if (modalState.mode === 'create') {
        await createCategory(payload).unwrap();
      } else if (modalState.mode === 'edit' && modalState.category?.id) {
        await updateCategory({
          id: modalState.category.id,
          data: payload,
        }).unwrap();
        refetch();
      }

      enqueueSnackbar(
        modalState.mode === 'create'
          ? 'Category created successfully'
          : 'Category updated successfully',
        { variant: 'success' }
      );
      handleCloseModal();
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors: Record<string, string> = {};
        err.inner.forEach((error) => {
          if (error.path) validationErrors[error.path] = error.message;
        });
        setErrors(validationErrors);
        enqueueSnackbar('Please fix the form errors', { variant: 'error' });
      } else {
        const errorMessage =
          (err as ErrorApiResponse)?.data?.message ||
          'Failed to process category';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteState.categoryId) return;
    try {
      await deleteCategory(confirmDeleteState.categoryId).unwrap();
      enqueueSnackbar('Category deleted successfully', { variant: 'success' });
      setConfirmDeleteState({ isOpen: false, categoryId: null });
      refetch();
    } catch (err) {
      const errorMessage =
        (err as ErrorApiResponse).data?.message || 'An error occurred';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setConfirmDeleteState({ isOpen: false, categoryId: null });
    }
  };

  const handleViewCategory = useCallback((category: IBerthCategoryData) => {
    setModalState({
      isOpen: true,
      category,
      mode: 'view',
    });
  }, []);

  const handleEditCategory = useCallback((category: IBerthCategoryData) => {
    setModalState({
      isOpen: true,
      category,
      mode: 'edit',
    });
  }, []);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBerthCategoryData } }) =>
          hasPermission('berth_category_management_read') ? (
            <button
              onClick={() => handleViewCategory(row.original)}
              className="text-sm font-medium text-gray-900 hover:text-primary-active focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label={`View ${row.original.name}`}
            >
              {row.original.name}
            </button>
          ) : (
            <span className="text-sm font-medium text-gray-900">
              {row.original.name}
            </span>
          ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBerthCategoryData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.description || '-'}
          </span>
        ),
      },
      {
        accessorKey: 'length_range',
        header: 'Length Range (m)',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBerthCategoryData } }) => {
          const { min_length, max_length } = row.original;
          const range = formatMeasurementRange(min_length, max_length);
          return <span className="text-gray-800 font-normal">{range}</span>;
        },
      },
      {
        accessorKey: 'width_range',
        header: 'Width Range (m)',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBerthCategoryData } }) => {
          const { min_width, max_width } = row.original;
          const range = formatMeasurementRange(min_width, max_width);
          return <span className="text-gray-800 font-normal">{range}</span>;
        },
      },
      {
        accessorKey: 'depth_range',
        header: 'Depth Range (m)',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBerthCategoryData } }) => {
          const { min_depth, max_depth } = row.original;
          const range = formatMeasurementRange(min_depth, max_depth);
          return <span className="text-gray-800 font-normal">{range}</span>;
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBerthCategoryData } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.status === 1
              ? 'Active'
              : row.original.status === 0
                ? 'Inactive'
                : 'Deleted'}
          </span>
        ),
      },
      {
        accessorKey: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: IBerthCategoryData } }) => (
          <div className="flex items-center gap-2">
            {hasPermission('berth_category_management_read') && (
              <button
                type="button"
                onClick={() => handleViewCategory(row.original)}
                className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="View"
                aria-label={`View ${row.original.name}`}
              >
                <KeenIcon type="filled" icon="eye" className="text-gray-500" />
              </button>
            )}
            {hasPermission('berth_category_management_update') && (
              <button
                type="button"
                onClick={() => handleEditCategory(row.original)}
                className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="Edit"
                aria-label={`Edit ${row.original.name}`}
              >
                <KeenIcon
                  type="filled"
                  icon="pencil"
                  className="text-gray-500"
                />
              </button>
            )}
            {hasPermission('berth_category_management_delete') && (
              <button
                type="button"
                onClick={() =>
                  setConfirmDeleteState({
                    isOpen: true,
                    categoryId: row.original.id,
                  })
                }
                className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="Delete"
                aria-label={`Delete ${row.original.name}`}
              >
                <KeenIcon type="filled" icon="trash" className="text-red-500" />
              </button>
            )}
          </div>
        ),
      },
    ],
    [handleViewCategory, handleEditCategory, hasPermission]
  );

  const filteredData: IBerthCategoryData[] = categoriesResponse?.data || [];

  return (
    <Fragment>
      {!isHarborDetailsRoute && <PageNavbar />}
      {currentLayout?.name === 'demo1-layout' && !isHarborDetailsRoute && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle text="Berth Category Management" />
              <ToolbarDescription>
                You can manage your berth categories here.
              </ToolbarDescription>
            </ToolbarHeading>
            {hasPermission('berth_category_management_create') && (
              <ToolbarActions>
                <button
                  type="button"
                  className="btn btn-sm btn-light"
                  onClick={() => handleOpenModal(null, 'create')}
                  aria-label="Add new category"
                >
                  Add New Category
                </button>
              </ToolbarActions>
            )}
          </Toolbar>
        </Container>
      )}
      <div className="card card-grid w-full">
        <div className="px-6 pt-5">
          <h1 className="text-base font-semibold text-gray-900">
            Berth Category Management
          </h1>
        </div>
        <div className="card-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
          <div className="flex-shrink-0">
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-category"
              aria-label="Search categories by name"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
              />
              <input
                id="search-category"
                placeholder="Search categories"
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search categories by name"
              />
            </label>
          </div>
          <div className="flex items-center gap-2">
            {isHarborDetailsRoute &&
              hasPermission('berth_category_management_create') && (
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    onClick={() => handleOpenModal(null, 'create')}
                    aria-label="Add new category"
                  >
                    Add New Category
                  </button>
                </div>
              )}
            {searchTerm && (
              <div className="flex items-center gap-2">
                <button
                  className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onClick={resetSearch}
                  aria-label="Clear search filter"
                  title="Clear search filter"
                >
                  <KeenIcon
                    type="solid"
                    icon="arrows-circle"
                    className="text-gray-600"
                  />
                  Clear Filter
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="card-body">
          {isLoading || isFetching ? (
            <div className="text-center py-4" role="status" aria-live="polite">
              Loading categories...
            </div>
          ) : isError ? (
            <div className="text-center py-4 text-red-500" role="alert">
              Failed to load categories. Please try again.
            </div>
          ) : (
            <DataGrid
              columns={columns}
              data={filteredData}
              rowSelect={false}
              loading={isLoading || isFetching}
              pagination={{
                page: pagination.pageIndex,
                size: pagination.pageSize,
                more: true,
                totalItems,
                totalPages,
                setPagination,
                info: `{from} - {to} of {count}`,
                sizes: [5, 10, 25, 50, 100],
                sizesLabel: 'Show',
                sizesDescription: 'per page',
              }}
              sorting={[{ id: 'name', desc: false }]}
              serverSide={true}
              onFilterReset={resetSearch}
              hasActiveFilters={!!searchTerm}
              aria-label="Berth categories table"
            />
          )}
          <BerthCategoryModal
            open={modalState.isOpen}
            onClose={handleCloseModal}
            mode={modalState.mode}
            categoryId={modalState.category?.id}
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
            onSubmit={handleSubmit}
            isSubmitting={isCreatingCategory}
            validateField={validateField}
          />
          <ConfirmationDialog
            open={confirmDeleteState.isOpen}
            onClose={() =>
              setConfirmDeleteState({ isOpen: false, categoryId: null })
            }
            onConfirm={handleDelete}
            message="Are you sure you want to delete this category?"
            cancelButtonText="No"
            confirmButtonText="Yes"
            aria-labelledby="delete-confirmation-title"
            aria-describedby="delete-confirmation-description"
          />
        </div>
      </div>
    </Fragment>
  );
};

export { BerthCategoryManagement };
